/**
 * Payment History Component
 * Displays payment records with TanStack Table
 * AC-3: Payment History Section
 */

import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  ColumnDef,
  SortingState,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CurrencyFormatter } from '@/components/common/CurrencyFormatter';
import { PaymentRecord, PaymentMethod, PaymentStatus } from '@/types';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { ArrowUpDown } from 'lucide-react';

interface PaymentHistoryProps {
  payments: PaymentRecord[];
}

const paymentMethodLabels: Record<PaymentMethod, string> = {
  [PaymentMethod.CASH]: 'Cash',
  [PaymentMethod.BANK_TRANSFER]: 'Bank Transfer',
  [PaymentMethod.UPI]: 'UPI',
  [PaymentMethod.CHEQUE]: 'Cheque',
  [PaymentMethod.CARD]: 'Card',
  [PaymentMethod.WALLET]: 'Wallet',
  [PaymentMethod.OTHER]: 'Other',
};

const paymentStatusColors: Record<PaymentStatus, string> = {
  [PaymentStatus.PENDING]: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  [PaymentStatus.COMPLETED]: 'bg-green-100 text-green-800 border-green-300',
  [PaymentStatus.FAILED]: 'bg-red-100 text-red-800 border-red-300',
  [PaymentStatus.REFUNDED]: 'bg-gray-100 text-gray-800 border-gray-300',
};

export const PaymentHistory = ({ payments }: PaymentHistoryProps) => {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'paymentDate', desc: true }, // Most recent first
  ]);

  const columns = useMemo<ColumnDef<PaymentRecord>[]>(
    () => [
      {
        accessorKey: 'paymentDate',
        header: ({ column }) => (
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Date
            <ArrowUpDown className="h-4 w-4" />
          </div>
        ),
        cell: ({ row }) => (
          <div className="font-medium">
            {format(new Date(row.original.paymentDate), 'dd MMM yyyy')}
          </div>
        ),
      },
      {
        accessorKey: 'amount',
        header: () => <div className="text-right">Amount</div>,
        cell: ({ row }) => (
          <div className="text-right">
            <CurrencyFormatter amount={row.original.amount} className="font-medium" />
          </div>
        ),
      },
      {
        accessorKey: 'paymentMethod',
        header: 'Method',
        cell: ({ row }) => (
          <div>{paymentMethodLabels[row.original.paymentMethod]}</div>
        ),
      },
      {
        accessorKey: 'transactionReference',
        header: 'Reference',
        cell: ({ row }) => (
          <div className="font-mono text-sm">
            {row.original.transactionReference || '-'}
          </div>
        ),
      },
      {
        accessorKey: 'runningBalance',
        header: () => <div className="text-right">Running Balance</div>,
        cell: ({ row }) => (
          <div className="text-right">
            <CurrencyFormatter
              amount={row.original.runningBalance}
              className={
                row.original.runningBalance > 0
                  ? 'text-red-600 font-medium'
                  : 'text-green-600 font-medium'
              }
            />
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.status;
          return (
            <Badge className={paymentStatusColors[status]}>
              {status}
            </Badge>
          );
        },
      },
    ],
    []
  );

  const table = useReactTable({
    data: payments,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (payments.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Payment History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <p>No payments recorded yet</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Payment History</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {payments.length > 0 && payments[0].notes && (
          <div className="mt-4 p-3 bg-muted rounded-md">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium">Latest Note:</span> {payments[0].notes}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
